import { injectable } from 'tsyringe';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';

/** A retention window: a positive day count, or `null` to keep forever. Mirrors @bmai/shared. */
export type RetentionValue = number | null;

/** Categories of data a retention window applies to. Mirrors @bmai/shared. */
export type RetentionCategory = 'telemetry' | 'audit' | 'insights' | 'licenses';

/** Sensitive-data drop toggles. Mirrors @bmai/shared `DataExclusionKey`. */
export type DataExclusionKey = 'promptResponseContent' | 'fileContents' | 'rawIpAddresses' | 'endUserPii';

export interface GovernancePolicy {
    tenantId: string;
    defaultRetentionDays: RetentionValue;
    retentionOverrides: Partial<Record<RetentionCategory, RetentionValue>>;
    exclusions: Record<DataExclusionKey, boolean>;
    updatedAt: string | null;
    updatedBy: string | null;
}

/** The editable shape sent on save (identity + audit columns are server-owned). */
export interface GovernancePolicyUpdate {
    defaultRetentionDays: RetentionValue;
    retentionOverrides: Partial<Record<RetentionCategory, RetentionValue>>;
    exclusions: Record<DataExclusionKey, boolean>;
}

const BASE = '/_billing/api/billing/governance-policy';

/**
 * Reads and writes the tenant's data-governance policy (Settings → Data &
 * Retention) via bmai-billing over the BFF, and triggers the data-only purge.
 */
@injectable()
export class GovernanceService {
    async getPolicy(): Promise<GovernancePolicy> {
        const res = await authenticatedFetch(BASE);
        if (!res.ok) throw new Error(await errorMessage(res, 'Failed to load data & retention settings'));
        return (await res.json()) as GovernancePolicy;
    }

    async updatePolicy(update: GovernancePolicyUpdate): Promise<GovernancePolicy> {
        const res = await authenticatedFetch(BASE, {
            method: 'PUT',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(update),
        });
        if (!res.ok) throw new Error(await errorMessage(res, 'Failed to save data & retention settings'));
        return (await res.json()) as GovernancePolicy;
    }

    async requestDataPurge(): Promise<void> {
        const res = await authenticatedFetch(`${BASE}/purge-data`, { method: 'POST' });
        if (!res.ok) throw new Error(await errorMessage(res, 'Failed to start data deletion'));
    }
}

async function errorMessage(res: Response, fallback: string): Promise<string> {
    try {
        const body = (await res.json()) as { error?: string };
        if (body.error) return body.error;
    } catch {
        void 0;
    }
    return `${fallback} (${res.status})`;
}
